import type { QuestionnaireItemAnswerOption } from 'fhir/r4';
import { useState, useEffect } from 'react';
import { Checkbox, Input, Radio } from 'semantic-ui-react';
import { QuestionnaireExtensionHelper } from 'utils/Helpers';

import { sanitizeQuestionnaireResponseAnswer, sanitizeQuestionnaireResponseAnswers } from '../fhir-helpers';

import type { QuestionnaireChoiceProps } from './item-props';

export default function OpenChoice(props: QuestionnaireChoiceProps) {
  //props: question, addAnswer, i, j, currentAnswer, enable

  const [selectedAnswer, setSelectedAnswer] = useState<fhir4.QuestionnaireResponseItemAnswer[]>([]);
  const extHelper = new QuestionnaireExtensionHelper(props.question?.extension);

  const [isOther, setIsOther] = useState(false);

  useEffect(() => {
    if (props.currentAnswer) {
      setSelectedAnswer(sanitizeQuestionnaireResponseAnswers(props.currentAnswer));
      const penultimate = props.currentAnswer[props.currentAnswer.length - 2];
      if (penultimate) {
        if (penultimate.valueCoding) {
          setIsOther(penultimate.valueCoding?.code === 'OTHER' || penultimate.valueCoding?.display === 'Other');
        } else {
          setIsOther(false);
        }
      } else {
        setIsOther(false);
      }
    } else {
      setSelectedAnswer([]);
      setIsOther(false);
    }
  }, [props]);

  const optionIndexInSelectedAnswer = (option: QuestionnaireItemAnswerOption) => {
    for (let i = 0; i < selectedAnswer.length; i++) {
      const selected = selectedAnswer[i]!;
      if (selected.valueCoding && option.valueCoding) {
        if (selected.valueCoding.code === option.valueCoding.code) {
          return i;
        }
      } else if (selected.valueInteger !== undefined && option.valueInteger !== undefined) {
        if (selected.valueInteger === option.valueInteger) {
          return i;
        }
      }
    }
    return -1;
  };

  const handleClick = (option: QuestionnaireItemAnswerOption) => {
    // A FHIR interface has no implicit index signature, so it does not satisfy the
    // helper's `Record<string, unknown>` parameter.
    const responseAnswer = sanitizeQuestionnaireResponseAnswer(option as unknown as Record<string, unknown>);

    if (extHelper.isCheckbox()) {
      const index = optionIndexInSelectedAnswer(option);
      const arr = JSON.parse(JSON.stringify(selectedAnswer)) as fhir4.QuestionnaireResponseItemAnswer[];
      if (index > -1) {
        if (option.valueCoding && (option.valueCoding?.code === 'OTHER' || option.valueCoding?.display === 'Other')) {
          arr.splice(index, 2);
          setIsOther(false);
        } else {
          arr.splice(index, 1);
        }
      } else {
        if (option.valueCoding && (option.valueCoding?.code === 'OTHER' || option.valueCoding?.display === 'Other')) {
          if (!responseAnswer) return;
          arr.push(responseAnswer, { valueString: '' });
          setIsOther(true);
        } else {
          if (!responseAnswer) return;
          arr.push(responseAnswer);
        }
      }
      arr.sort((a, b) => {
        if (a.valueCoding === undefined) {
          return 1;
        }
        if (b.valueCoding === undefined) {
          return -1;
        }
        // SUSPECTED DEFECT — REPORTED, NOT FIXED. Same `NaN` comparator as `choice.tsx`;
        // the two `undefined` guards above run, but this final comparison never reorders.
        return (a.valueCoding.code as unknown as number) - (b.valueCoding.code as unknown as number);
      });
      setSelectedAnswer(arr);
      props.addAnswer(sanitizeQuestionnaireResponseAnswers(arr), props.i, props.j);
    } else {
      if (!responseAnswer) return;
      setSelectedAnswer([responseAnswer]);
      props.addAnswer([responseAnswer], props.i, props.j);
      setIsOther(false);
      if (option.valueCoding) {
        if (option.valueCoding?.code === 'OTHER' || option.valueCoding?.display === 'Other') {
          setSelectedAnswer([responseAnswer, { valueString: '' }]);
          props.addAnswer([responseAnswer, { valueString: '' }], props.i, props.j);
          setIsOther(true);
        }
      }
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let arr = JSON.parse(JSON.stringify(selectedAnswer)) as fhir4.QuestionnaireResponseItemAnswer[];
    if (isOther) {
      // Asserted, not guarded: `isOther` implies the "Other" pair was pushed, and an
      // empty `arr` must keep throwing.
      arr[arr.length - 1]!.valueString = e.target.value;
    } else {
      if (!extHelper.isCheckbox()) {
        // Radio mode: free-text is mutually exclusive with coded options
        arr = e.target.value === '' ? [] : [{ valueString: e.target.value }];
      } else {
        // Checkbox mode: keep existing append behavior
        if (arr[arr.length - 1]?.valueString || arr[arr.length - 1]?.valueString === '') {
          if (e.target.value === '' && arr[arr.length - 1]?.valueString) {
            arr.pop();
          } else {
            arr[arr.length - 1]!.valueString = e.target.value;
          }
        } else {
          arr.push({ valueString: e.target.value });
        }
      }
    }
    const sanitized = sanitizeQuestionnaireResponseAnswers(arr);
    setSelectedAnswer(sanitized);
    props.addAnswer(sanitized, props.i, props.j);
  };

  const handleLabel = (option: QuestionnaireItemAnswerOption) => {
    let displayValue: string | number | undefined;
    // See the note on the same line in `choice.tsx`: passing an absent language
    // through is what already happens, and it selects the `display` branch.
    const chosenLang = props.language!;
    if (option?.valueCoding) {
      const extHelper = new QuestionnaireExtensionHelper(option?.valueCoding?.extension);
      if (extHelper.getTargetTranslation(chosenLang)) {
        displayValue = extHelper.getTargetTranslation(chosenLang);
      } else {
        displayValue = option.valueCoding.display;
      }
    } else {
      displayValue = option?.valueInteger;
    }
    return displayValue;
  };

  /*
   * SUSPECTED DEFECT — `props.question.answerOption` is dereferenced unguarded
   * here and at the `.length` read below, but `Questionnaire.item.answerOption` is
   * 0..* in FHIR. An open-choice question served without options throws
   * "Cannot read properties of undefined (reading 'map')". The sibling
   * `choice.tsx` guards the identical expression, which makes this look like an
   * oversight — but what an option-less open-choice should render is a product
   * decision, so the assertions preserve the throw.
   */
  return (
    <div>
      {props.question.answerOption!.map((option, i) => (
        <div key={i}>
          {((!extHelper.isEmpty() && extHelper.isCheckbox()) || extHelper.getItemControlCode() === undefined) && (
            <Checkbox
              disabled={!props.enable}
              label={handleLabel(option)}
              checked={optionIndexInSelectedAnswer(option) > -1}
              onChange={() => handleClick(option)}
            />
          )}
          {!extHelper.isEmpty() && extHelper.isRadioButton() && (
            <Radio
              disabled={!props.enable}
              label={handleLabel(option)}
              checked={optionIndexInSelectedAnswer(option) > -1}
              onChange={() => handleClick(option)}
            />
          )}
          {option.valueCoding &&
            (option.valueCoding?.code === 'OTHER' || option.valueCoding?.display === 'Other') &&
            (isOther ? (
              <>
                <Input
                  disabled={!props.enable}
                  fluid
                  focus
                  placeholder="Enter Answer Here"
                  // Reachable only under `isOther`, which `handleClick` sets at the
                  // same moment it pushes the trailing `{ valueString: '' }` — so the
                  // element is present whenever this renders. Asserting keeps the
                  // existing throw if that invariant is ever broken.
                  value={selectedAnswer[selectedAnswer.length - 1]!.valueString}
                  onChange={handleInput}
                />
              </>
            ) : (
              <Input fluid focus placeholder="Enter Answer Here" value="" disabled />
            ))}
          {i === props.question.answerOption!.length - 1 ? (
            <>
              {option.valueCoding?.code !== 'OTHER' && option.valueCoding?.display !== 'Other' && <br></br>}
              {`Other: `}
              {
                <Input
                  disabled={!props.enable}
                  fluid
                  focus
                  placeholder="Enter Answer Here"
                  value={selectedAnswer[selectedAnswer.length - 1]?.valueString ?? ''}
                  onChange={handleInput}
                />
              }{' '}
            </>
          ) : (
            <br></br>
          )}
        </div>
      ))}
    </div>
  );
}
